import * as React from 'react';

import { omit as _omit, orderBy as _orderBy } from 'lodash';

import { UniqueIdentifier } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';

import { DateTime } from 'luxon';
import useGetUnattachedEventReports from '../api/hooks/event-report/useGetUnattachedEventReports';
import { EventReport } from '../api/clients';

interface UnattachedEventReportsProviderProps {
  children: React.ReactNode;
  cutoffDate: string;
}

type EventReportsRecord = Record<string, EventReport[]>;
type RecordKeys = Record<string, string>;

type RecordReducer = {
  record: EventReportsRecord;
  keys: RecordKeys;
};

type UnattachedEventReportsData = {
  isLoading: boolean;
  isLargeDataSet: boolean;
  hasReports: boolean;
  groupedEventReports: EventReportsRecord;
  individualEventReports: EventReport[];
  getReport: (collectionId: string, reportId: UniqueIdentifier) => EventReport | undefined;
  moveReport: (reportId: UniqueIdentifier, fromContainerId: UniqueIdentifier, toContainerId: UniqueIdentifier, overItemId: UniqueIdentifier | undefined) => void;
  endMoveReport: (fromContainerId: UniqueIdentifier, toContainerId: UniqueIdentifier, activeReportId: UniqueIdentifier, overReportId: UniqueIdentifier | undefined) => void;
};

const UnattachedEventReportsContext = React.createContext<UnattachedEventReportsData>(undefined!);

export const INDIVIDUAL_REPORTS_COLLECTION_ID = 'INDIVIDUAL';

export function UnattachedEventReportsProvider({ children, cutoffDate }: UnattachedEventReportsProviderProps) {
  const { data, status, error, isLoading, isFetching, refetch } = useGetUnattachedEventReports(cutoffDate);
  const [eventReportRecord, setEventReportRecord] = React.useState<EventReportsRecord>(undefined!);
  const [isLargeDataSet, setIsLargeDataSet] = React.useState(false);
  React.useEffect(() => {
    if (status === 'success' && data) {
      // Group the event reports into records
      const recordWithKeys = data.reduce((recordReducer: RecordReducer, eventReport: EventReport) => {
        const key: string = [
          eventReport.flightNumber,
          eventReport.eventDate,
          eventReport.departureAirport,
          eventReport.scheduledArrivalAirport,
          eventReport.aircraftNumber,
        ].join('|');

        const keyValue: string = recordReducer.keys[key] ? recordReducer.keys[key] : crypto.randomUUID();

        return {
          keys: { ...recordReducer.keys, [key]: keyValue },
          record: { ...recordReducer.record, [keyValue]: [...recordReducer.record[keyValue] ?? [], eventReport] },
        };
      }, { record: {} as EventReportsRecord, keys: {} as RecordKeys });

      // Extract reports from the groups with only one report to a single "Individual Reports" group
      const finalRecord = Object.keys(recordWithKeys.record).reduce((record: EventReportsRecord, key: string) => {
        if (recordWithKeys.record[key].length > 1) {
          return {
            ...record,
            [key]: _orderBy(recordWithKeys.record[key], ['order'], ['desc']),
          };
        }

        return {
          ...record,
          [INDIVIDUAL_REPORTS_COLLECTION_ID]: [
            ...record[INDIVIDUAL_REPORTS_COLLECTION_ID] ?? [],
            recordWithKeys.record[key][0],
          ],
        };
      }, {} as EventReportsRecord);
      setIsLargeDataSet(data.length > 70);
      setEventReportRecord(finalRecord);
    }
  }, [data, status, error, isFetching]);

  React.useEffect(() => {
    refetch();
  }, [cutoffDate]);

  const unattachedEventReportsData = React.useMemo<UnattachedEventReportsData>(() => ({
    isLoading: isLoading || isFetching,
    isLargeDataSet,
    hasReports: eventReportRecord ? Object.keys(eventReportRecord).length > 0 : false,
    groupedEventReports: _omit(eventReportRecord, [INDIVIDUAL_REPORTS_COLLECTION_ID]),
    individualEventReports: eventReportRecord ? eventReportRecord[INDIVIDUAL_REPORTS_COLLECTION_ID] : [] as EventReport[],
    // eslint-disable-next-line max-len
    getReport: (collectionId: string, reportId: UniqueIdentifier) => eventReportRecord[collectionId].find((e: EventReport) => e.id === reportId),
    // eslint-disable-next-line max-len
    moveReport: (reportId: UniqueIdentifier, fromContainerId: UniqueIdentifier, toContainerId: UniqueIdentifier, overItemId: UniqueIdentifier | undefined) => {
      setEventReportRecord((record: EventReportsRecord) => {
        const fromIndex = record[fromContainerId].findIndex((i: EventReport) => i.id === reportId);
        const toIndex = record[toContainerId].findIndex((i) => i.id !== overItemId);

        return {
          ...record,
          [fromContainerId]: [
            ...record[fromContainerId].filter((i) => i.id !== reportId),
          ],
          [toContainerId]: [
            ...record[toContainerId].slice(0, toIndex),
            record[fromContainerId][fromIndex],
            ...record[toContainerId].slice(toIndex, record[toContainerId].length),
          ],
        };
      });
    },
    endMoveReport: (fromContainerId, toContainerId, activeReportId, overReportId) => {
      const fromIndex = eventReportRecord[fromContainerId].findIndex((i) => i.id === activeReportId);
      const toIndex = eventReportRecord[toContainerId].findIndex((i) => i.id === overReportId);

      if (fromIndex !== toIndex) {
        setEventReportRecord((record) => ({
          ...record,
          [toContainerId]: arrayMove(
            record[toContainerId],
            fromIndex,
            toIndex,
          ),
        }));
      }
    },
  }), [eventReportRecord]);

  return (
    <UnattachedEventReportsContext.Provider value={unattachedEventReportsData}>
      {children}
    </UnattachedEventReportsContext.Provider>
  );
}

export const useUnattachedEventReportsData = () => React.useContext(UnattachedEventReportsContext);
